import Draggable from "./Draggable";
import HiddenPdfLoader from "./HiddenPdfLoader";
import CloseIcon from "../Icons/CloseIcon";
import { isNativeMode } from "../../lib/nativeMode";

interface DetachablePdfWindowProps {
  open: boolean;
  onClose: () => void;
  title: string;
  src?: string;
  /** Offset from the viewport corner, so stacked windows don't fully overlap. */
  offset?: number;
}

/**
 * Floating, draggable PDF viewer, matching the window the CBQ exam uses for
 * scenario PDFs.
 */
const DetachablePdfWindow = ({
  open,
  onClose,
  title,
  src,
  offset = 0,
}: DetachablePdfWindowProps) => {
  if (!open || !src) return null;

  // Under `?isMobile` the host app presents the PDF, so the window is loaded
  // but never shown. Same trigger, same request, no visible chrome.
  if (isNativeMode()) return <HiddenPdfLoader src={src} title={title} />;

  return (
    // `cancel` is required: the header is the drag handle, and react-draggable
    // preventDefault()s touchstart inside the handle, which stops a tap from
    // ever becoming a click. Without this the close button is dead on mobile.
    <Draggable handle=".dw-header" cancel=".dw-close">
      <div
        className="fixed z-[9999] flex flex-col bg-white border border-black/20 rounded-lg shadow-2xl overflow-hidden"
        style={{
          top: 96 + offset,
          left: 32 + offset,
          width: "min(90vw, 680px)",
          height: "min(70vh, 560px)",
          resize: "both",
        }}
      >
        <div className="dw-header cursor-move bg-[#4d4c4d] text-white px-3 py-2 flex items-center justify-between select-none">
          <span className="text-sm font-semibold">{title}</span>
          <button
            type="button"
            className="dw-close"
            onClick={onClose}
            title="Close"
          >
            <CloseIcon fill="#fff" />
          </button>
        </div>
        <iframe src={src} title={title} className="flex-1 w-full border-0" />
      </div>
    </Draggable>
  );
};

export default DetachablePdfWindow;
